import React, { useState } from 'react';
import { Package, ChevronDown, ChevronUp } from 'lucide-react';

interface VisitBatchItem {
  needs_followup?: boolean;
  followup_materials?: string[];
  followup_actions?: string[];
  pos_name: string;
}

interface MaterialsSummaryProps {
  items: VisitBatchItem[];
}

const MATERIAL_LABELS: Record<string, string> = {
  pricetags: 'Pricetags',
  wobblers: 'Wobblers',
  posters: 'Posters',
  flyers: 'Flyers',
  merch: 'Merch',
};

export const MaterialsSummary: React.FC<MaterialsSummaryProps> = ({ items }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Aggregate materials from all follow-up items
  const materialsNeeded = items
    .filter(item => item.needs_followup && item.followup_materials)
    .flatMap(item => item.followup_materials || [])
    .reduce((acc, mat) => {
      acc[mat] = (acc[mat] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  const hasMaterials = Object.keys(materialsNeeded).length > 0;

  if (!hasMaterials) {
    return null; // Don't render if no materials needed in this route
  }

  return (
    <div className="mb-6 bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-2xl overflow-hidden shadow-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-purple-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Package className="text-purple-600" size={24} />
          <div className="text-left">
            <h3 className="font-black text-gray-900">Materials Checklist for This Route</h3>
            <p className="text-sm text-gray-600">
              {Object.keys(materialsNeeded).length} material type{Object.keys(materialsNeeded).length !== 1 ? 's' : ''} needed
            </p>
          </div>
        </div>
        {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6">
          {/* Materials */}
          <div>
            <h4 className="font-bold text-purple-800 mb-3 text-sm">MATERIALS TO PACK</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(materialsNeeded).map(([mat, count]) => (
                <div key={mat} className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-gray-300 text-purple-600"
                  />
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 text-sm">{MATERIAL_LABELS[mat] || mat}</div>
                    <div className="text-xs text-gray-500">{count} stop{count > 1 ? 's' : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
